import { type ReactNode, useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { IntakePageFooterPortalContext } from './IntakePageFooterPortalContext';
import { FLOW_INTAKE_SHELL_HEIGHT, IntakeShellHeightContext } from './IntakeShellHeightContext';
import { PHASES, RAIL_SECTIONS } from './navigation';
import { wf } from './tokens';

export type RailProgressState = 'notStarted' | 'inProgress' | 'completed';
type RailState = RailProgressState | 'locked';

interface IntakeShellProps {
  organizationName: string;
  currentPageKey: string;
  /** Page keys the customer's config does not require. */
  notNeededKeys: Set<string>;
  /** True until the config gate is answered; everything past K1 stays locked. */
  gateOpen: boolean;
  percentComplete: number;
  onNavigate: (pageKey: string) => void;
  /** Suggestions waiting on each page, so the rail can show where to look. */
  pendingByPage?: Record<string, { total: number; unconfirmed: number }>;
  /** Durable field completion for each generated page. */
  progressByPage?: Record<string, RailProgressState>;
  /** Pages that remain visible but cannot be opened yet. */
  lockedPageKeys?: Set<string>;
  /** Drop the title bar and phase strip when a parent journey already shows both. */
  hideHeader?: boolean;
  children: ReactNode;
}

function LockIcon() {
  return (
    <svg width="11" height="12" viewBox="0 0 12 14" fill="none" aria-hidden="true">
      <rect x="1.5" y="6" width="9" height="6.5" rx="1.5" stroke={wf.faint} strokeWidth="1.4" />
      <path d="M3.5 6V4.2a2.5 2.5 0 0 1 5 0V6" stroke={wf.faint} strokeWidth="1.4" />
    </svg>
  );
}

function Dot({ isCurrent, state }: { isCurrent: boolean; state: RailState }) {
  if (state === 'locked') return <LockIcon />;
  if (state === 'completed') {
    return (
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M3 8.5 6.5 12 13 4.5"
          stroke={wf.green}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  const filled = state === 'inProgress' || isCurrent;
  return (
    <Box
      component="span"
      sx={{
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        display: 'block',
        background: filled ? wf.blue : 'transparent',
        border: filled ? 'none' : `1.4px solid ${wf.faint}`,
      }}
    />
  );
}

export function IntakeShell({
  organizationName,
  currentPageKey,
  notNeededKeys,
  gateOpen,
  percentComplete,
  onNavigate,
  pendingByPage = {},
  progressByPage = {},
  lockedPageKeys = new Set(),
  hideHeader = false,
  children,
}: IntakeShellProps) {
  // Never hardcode this height again — anything stacked above the shell has to
  // hand it a slot through IntakeShellHeightContext, or the sticky footer slot
  // below gets pushed past the bottom of the viewport.
  const shellHeight = useContext(IntakeShellHeightContext);
  const flowsWithPage = shellHeight === FLOW_INTAKE_SHELL_HEIGHT;
  const [footerContainer, setFooterContainer] = useState<HTMLElement | null>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  useEffect(() => {
    if (!footerContainer) {
      setFooterHeight(0);
      return;
    }
    // The sticky footer slot's content (Back/Continue) varies by page, so its
    // height isn't a fixed constant — track it so the scrolling main can
    // reserve exactly that much space and never leave content permanently
    // hidden behind the footer in the embedded case.
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setFooterHeight(entry.contentRect.height);
    });
    observer.observe(footerContainer);
    return () => observer.disconnect();
  }, [footerContainer]);
  const visibleSections = RAIL_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => !notNeededKeys.has(item.pageKey)),
  })).filter(section => section.items.length > 0);
  const visiblePhases = PHASES.filter(phase =>
    visibleSections.some(section => section.phase === phase),
  );
  const currentPhase =
    visibleSections.find(section => section.items.some(item => item.pageKey === currentPageKey))
      ?.phase ?? PHASES[0];

  const stateFor = (pageKey: string): RailState => {
    if (lockedPageKeys.has(pageKey) || (!gateOpen && pageKey !== 'K1')) {
      return 'locked';
    }
    return progressByPage[pageKey] ?? 'notStarted';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: shellHeight,
        color: wf.ink,
        background: wf.surface,
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      {!hideHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            borderBottom: `1px solid ${wf.line}`,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '14px', minWidth: 0 }}>
            JotPsych Intake{' '}
            <Box
              component="span"
              sx={{
                color: wf.muted,
                fontWeight: 400,
                display: { xs: 'none', sm: 'inline' },
              }}
            >
              · {organizationName}
            </Box>
          </Typography>
        </Box>
      )}

      {!hideHeader && (
        <Box sx={{ borderBottom: `1px solid ${wf.line}`, padding: '8px 16px 0', flexShrink: 0 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Box sx={{ display: 'flex', gap: '18px', overflowX: 'auto', minWidth: 0 }}>
              {visiblePhases.map(phase => {
                const isCurrent = phase === currentPhase;
                const phaseItems = visibleSections
                  .filter(section => section.phase === phase)
                  .flatMap(section => section.items);
                const isComplete =
                  !isCurrent &&
                  phaseItems.length > 0 &&
                  phaseItems.every(item => stateFor(item.pageKey) === 'completed');
                return (
                  <Box
                    key={phase}
                    data-phase-state={isCurrent ? 'current' : isComplete ? 'completed' : 'upcoming'}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '11.5px',
                      paddingBottom: '6px',
                      color: isCurrent ? wf.blue : isComplete ? wf.ink : wf.muted,
                      fontWeight: isCurrent ? 700 : 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isComplete ? (
                      <Dot isCurrent={false} state="completed" />
                    ) : (
                      <Box
                        component="span"
                        sx={{
                          width: '9px',
                          height: '9px',
                          borderRadius: '50%',
                          background: isCurrent ? wf.blue : 'transparent',
                          border: isCurrent ? 'none' : `1.4px solid ${wf.faint}`,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {phase}
                  </Box>
                );
              })}
            </Box>
            <Box
              sx={{
                fontSize: '11.5px',
                color: wf.muted,
                paddingBottom: '8px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {percentComplete}% complete
            </Box>
          </Box>
          <Box
            role="progressbar"
            aria-label="Intake completion"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percentComplete}
            sx={{ height: '6px', background: wf.track, margin: '0 -16px' }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${percentComplete}%`,
                background: percentComplete >= 100 ? wf.green : wf.blue,
              }}
            />
          </Box>
        </Box>
      )}

      <IntakePageFooterPortalContext.Provider value={footerContainer}>
        <Box sx={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
          <Box
            component="nav"
            aria-label="Intake sections"
            sx={{
              width: flowsWithPage ? { md: 260, lg: 280 } : 230,
              flexShrink: 0,
              borderRight: flowsWithPage ? 'none' : `1px solid ${wf.line}`,
              padding: flowsWithPage ? '16px 8px' : '12px 10px',
              overflowY: 'auto',
              background: flowsWithPage ? wf.surface : wf.surfaceAlt,
              display: { xs: 'none', md: 'block' },
              ...(flowsWithPage
                ? {
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    maxHeight: 'var(--app-content-height, 100dvh)',
                  }
                : {}),
            }}
          >
            {visibleSections.map(section => {
              const completedItems = section.items.filter(
                item => stateFor(item.pageKey) === 'completed',
              );
              const sectionPercentComplete = Math.round(
                (completedItems.length / section.items.length) * 100,
              );

              return (
                <Box key={section.key} sx={flowsWithPage ? { mb: 3 } : undefined}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      fontSize: flowsWithPage ? '12px' : '10px',
                      letterSpacing: '.08em',
                      color: wf.muted,
                      textTransform: 'uppercase',
                      margin: flowsWithPage ? '0 10px 8px' : '12px 6px 4px',
                    }}
                  >
                    <Box component="span" sx={{ minWidth: 0 }}>
                      {section.label}
                    </Box>
                    <Box
                      component="span"
                      aria-label={`${section.label} pages complete`}
                      sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {completedItems.length}/{section.items.length}
                    </Box>
                  </Box>
                  <Box
                    role="progressbar"
                    aria-label={`${section.label} completion`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={sectionPercentComplete}
                    sx={{
                      display: flowsWithPage ? 'none' : 'block',
                      height: '3px',
                      borderRadius: '2px',
                      background: '#e3e5e9',
                      margin: '2px 6px 6px',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${sectionPercentComplete}%`,
                        borderRadius: '2px',
                        background: wf.blue,
                        transition: 'width 180ms ease-out',
                      }}
                    />
                  </Box>
                  {section.items.map(item => {
                    const state = stateFor(item.pageKey);
                    const isCurrent = item.pageKey === currentPageKey;
                    const interactive = state !== 'locked';
                    return (
                      <Box
                        key={item.pageKey}
                        role={interactive ? 'button' : undefined}
                        aria-current={isCurrent ? 'step' : undefined}
                        data-progress-state={state}
                        tabIndex={interactive ? 0 : undefined}
                        onClick={() => interactive && onNavigate(item.pageKey)}
                        onKeyDown={event => {
                          if (interactive && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            onNavigate(item.pageKey);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: flowsWithPage ? '12px' : '8px',
                          padding: flowsWithPage ? '8px 10px' : '5px 8px',
                          borderRadius: flowsWithPage ? '8px' : '6px',
                          fontSize: flowsWithPage ? '14px' : '12.5px',
                          lineHeight: flowsWithPage ? 1.5 : undefined,
                          cursor: interactive ? 'pointer' : 'default',
                          background: isCurrent ? wf.blueSoft : 'transparent',
                          fontWeight: isCurrent ? 600 : 400,
                          color: isCurrent
                            ? wf.blue
                            : state === 'notStarted'
                              ? wf.muted
                              : state === 'inProgress' || state === 'completed'
                                ? wf.ink
                                : wf.faint,
                          '&:hover': interactive
                            ? { background: isCurrent ? wf.blueSoft : wf.surfaceAlt }
                            : {},
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: '14px',
                            height: '14px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Dot isCurrent={isCurrent} state={state} />
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            flexGrow: 1,
                            minWidth: 0,
                          }}
                        >
                          {item.label}
                        </Box>
                        {(pendingByPage[item.pageKey]?.unconfirmed ?? 0) > 0 && (
                          <Box
                            component="span"
                            aria-label={`${pendingByPage[item.pageKey]?.unconfirmed} values to review`}
                            sx={{
                              flexShrink: 0,
                              minWidth: '17px',
                              height: '17px',
                              padding: '0 5px',
                              borderRadius: '9px',
                              // Blue, matching the suggestion chips these count
                              // — an amber badge read as a warning about the
                              // page rather than a pointer at suggestions.
                              background: wf.blueSoft,
                              border: `1px solid ${wf.blue}`,
                              color: wf.blue,
                              fontSize: '10px',
                              fontWeight: 800,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {pendingByPage[item.pageKey]?.unconfirmed}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', flex: '1 1 auto', minWidth: 0, flexDirection: 'column' }}>
            <Box
              component="main"
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                padding: { xs: '16px', sm: '20px 26px' },
                // Reserve space for the sticky footer slot below so the last
                // strip of content isn't permanently hidden behind it in the
                // embedded case, where the footer sticks inside this
                // scrollport rather than the viewport.
                paddingBottom: {
                  xs: `calc(16px + ${footerHeight}px)`,
                  sm: `calc(20px + ${footerHeight}px)`,
                },
                overflowY: flowsWithPage ? 'visible' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
              }}
            >
              {children}
            </Box>
            <Box
              ref={setFooterContainer}
              data-testid="intake-page-footer-slot"
              sx={{
                flex: '0 0 auto',
                // Keep Back/Continue at the bottom of the nearest scrollport
                // in both bounded embeds and flowing customer intake pages.
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
                background: wf.surface,
              }}
            />
          </Box>
        </Box>
      </IntakePageFooterPortalContext.Provider>
    </Box>
  );
}
